import React, { useEffect } from 'react';
import { Alert, Card, Container, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { connect } from 'react-redux';

import Option from './option_defaults/option';
import ManagedInput from 'components/form/managed_input';

import PagePicker from './page_picker';
import { setFormData } from './store/actions';
import { saveFormData } from './api';
import AvailabilityPicker from './availability_picker';
import CountryPicker from './country_picker';
import PropertyTypePicker from './property_type_picker';
import UserPicker from './user_picker';
import PanelHeader from './shared/panel_header';
import { faBuilding, faFile, faGlobe, faUsers, faWrench } from '@fortawesome/pro-solid-svg-icons';
import DefaultOptionMessage from './shared/default_option_message';
import Header from './shared/header';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type DocumentDefaultsType, type DocumentTemplateType } from './types';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import SaveDocumentTemplateModal from './shared/save_document_template_modal';

type DocumentTemplateFormProps = {
  afterSavePath: string,
  basePath: string,
  companyId?: number,
  formData: DocumentDefaultsType,
  setFormData: (formData: DocumentDefaultsType) => void,
  template: DocumentTemplateType
};

type PageSettingsItem = {
  id: number,
  template_type?: string
};

type PageSettingsType = {
  availablePageTemplateTreeIds: PageSettingsItem[] | null,
  defaultPages: PageSettingsItem[] | null
};

export function DocumentTemplateForm({
  afterSavePath,
  basePath,
  companyId,
  formData,
  template,
  setFormData
}: DocumentTemplateFormProps) {
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [showSaveModal, setShowSaveModal] = React.useState(false);
  const initialPageSettings = React.useRef(extractPageSettings(formData));

  useEffect(() => {
    const onUnload = (event) => {
      if (dirty) event.returnValue = true;
    };

    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const handleReturn = () => window.location.href = afterSavePath;

  // can't be called in `saveFormData(...).then` because setDirty changes `dirty` on the *next* render
  if (saved) handleReturn();

  const save = (propagateToChildren = false) => {
    const putData: DocumentDefaultsType = {
      ...formData
    };

    if (companyId != null) putData.companyId = companyId;

    if (putData.defaultPages) {
      putData.defaultPages = putData.defaultPages.map(({ id, template_type }) => ({ id, template_type } as any));
    }
    if (putData.availablePageTemplateTreeIds) {
      putData.availablePageTemplateTreeIds = putData.availablePageTemplateTreeIds.map(({ id, template_type }) => (
        { id, template_type } as any
      ));
    }

    setSaving(true);
    saveFormData(basePath, putData, { propagateToChildren }).then(() => {
      setDirty(false);
      setSaved(true);
      setShowSaveModal(false);
    }).catch(() => {
      setSaving(false);
    });
  };

  const handleSave = () => {
    if (
      template.hasChildTemplates &&
      pageSettingsChanged(initialPageSettings.current, extractPageSettings(formData))
    ) {
      setShowSaveModal(true);
      return;
    }

    save(false);
  };

  const handleFormUpdate = (newFormData : DocumentDefaultsType) => {
    setDirty(true);
    setFormData(newFormData);
  };

  const updateName = (docName : string) => {
    handleFormUpdate({ ...formData, docName });
  };

  const handleUpdatePropertyType = (
    { propertyTypes,
      propertySubtypes,
      selectAllPropertyTypes } : {
      propertyTypes?: number[],
      propertySubtypes?: number[],
      selectAllPropertyTypes?: boolean
    }
  ) => {
    const payload = { ...formData };
    if (selectAllPropertyTypes) {
      payload.selectAllPropertyTypes = true;
      payload.propertyTypes = null;
      payload.propertySubtypes = null;
    } else {
      payload.selectAllPropertyTypes = false;
      payload.propertyTypes = propertyTypes || null;
      payload.propertySubtypes = propertySubtypes || null;
    }

    handleFormUpdate(payload);
  };

  const handleUpdateOption = (changes) => {
    const docOptions = { ...formData.docOptions, ...changes };

    handleFormUpdate({ ...formData, docOptions });
  };

  const handleRemoveOptions = (names) => {
    const docOptions = { ...formData.docOptions };
    names.forEach((name) => {
      delete docOptions[name];
    });

    handleFormUpdate({ ...formData, docOptions });
  };

  const handleUserChange = ({ userIds, selectAllUserIds }) => {
    const payload = { ...formData };
    if (selectAllUserIds) {
      payload.selectAllUserIds = true;
      payload.userIds = null;
    } else {
      payload.selectAllUserIds = false;
      payload.userIds = userIds;
    }

    handleFormUpdate(payload);
  };

  const handleUpdateAvailability = (
    { availability,
      enableForSaleAndLease } : {
      availability?: string[],
      enableForSaleAndLease?: boolean
    }
  ) => {
    const payload = { ...formData };
    if (enableForSaleAndLease) {
      payload.enableForSaleAndLease = true;
      payload.availability = 'sale_and_lease';
    } else {
      payload.enableForSaleAndLease = false;
      // Convert array to enum value
      if (availability && availability.length > 0) {
        if (availability.includes('sale') && availability.includes('lease')) {
          payload.availability = 'sale_and_lease';
        } else if (availability.includes('sale')) {
          payload.availability = 'sale';
        } else if (availability.includes('lease')) {
          payload.availability = 'lease';
        }
      } else {
        // When both are unchecked, set to null
        payload.availability = null;
      }
    }

    handleFormUpdate(payload);
  };

  const handleUpdateCountry = (
    { countryIds,
      selectAllCountryIds } : {
      countryIds?: number[],
      selectAllCountryIds?: boolean
    }
  ) => {
    const payload = { ...formData };
    if (selectAllCountryIds) {
      payload.selectAllCountryIds = true;
      payload.countryIds = null;
    } else {
      payload.selectAllCountryIds = false;
      payload.countryIds = countryIds || null;
    }

    handleFormUpdate(payload);
  };

  const options = template.options.map((option, i) => {
    return (
      <div className={i < template.options.length - 1 ? 'mb-3' : ''} key={option.name}>
        <Option
          defaults={formData.docOptions}
          option={option}
          onRemove={handleRemoveOptions}
          onUpdate={handleUpdateOption}
        />
      </div>
    );
  });

  const parentTemplateInfo = (
    <Alert className="mt-4" variant="info">
      <OverlayTrigger
        overlay={
          <Tooltip id="info-link-tooltip">
            {`
              Duplicated documents inherit their look & feel, margins, options,
              and other capabilities from the original document they are duplicated from.
              Click to view parent document.
            `}
          </Tooltip>
        }
        placement="bottom"
      >
        <a
          className="d-inline-flex align-items-center gap-1 text-decoration-none"
          href={template.parentTemplateEditPath}
          rel="noopener noreferrer"
          target="_blank"
        >
          <FontAwesomeIcon className="text-primary" icon={faCircleInfo} size="sm" />
          This document was duplicated. It inherits capabilities from{' '}
          <strong className="fw-semibold">{template.parentTemplateName}</strong>
        </a>
      </OverlayTrigger>
    </Alert>
  );

  return (
    <div>
      <Header
        modelName="Document Template"
        saving={saving}
        templateName={formData.docName || template.name}
        onCancel={handleReturn}
        onSave={handleSave}
      />
      <Container className="mb-8">
        <Card className="p-5">
          <Card.Body>
            <div className="mb-7">
              <h6 className="my-4">Document Settings</h6>
              <p className="fw-semibold my-2">Document Name</p>
              <ManagedInput
                className="form-control w-50"
                name="name-input"
                placeholder={template.name}
                value={formData.docName}
                onChange={change => updateName(change['name-input'])}
              />
              {template.parentTemplateName && parentTemplateInfo}
              <h6 className="mt-7">Default Behavior</h6>
              <p className="my-2">
                Below, you are able to set the default behavior for this document template.
                You can choose which properties it is available for, which doc options it has,
                which users have access to it, and which pages appear by default.
                Any changes you make will be seen when a new version of this document is created.
              </p>
            </div>
            <Card className="mb-7">
              <PanelHeader
                icon={faBuilding}
                subtitle="Choose whether this document is available for sale or lease"
                title="Availability"
              />
              <Card.Body>
                <AvailabilityPicker
                  defaultAvailability={template.parameters.availability}
                  enableForSaleAndLease={formData.enableForSaleAndLease}
                  selectedAvailability={formData.availability}
                  onUpdateAvailability={handleUpdateAvailability}
                />
              </Card.Body>
            </Card>
            <Card className="mb-7">
              <PanelHeader
                icon={faGlobe}
                subtitle="Choose which countries this document is available for"
                title="Country"
              />
              <Card.Body>
                <CountryPicker
                  defaultCountryIds={template.parameters.countryIds}
                  selectAllCountryIds={formData.selectAllCountryIds}
                  selectedCountryIds={formData.countryIds}
                  onUpdateCountry={handleUpdateCountry}
                />
              </Card.Body>
            </Card>
            <Card className="mb-7" id="default-doc-property-types">
              <PanelHeader
                icon={faBuilding}
                subtitle="Choose which property types this document is available for"
                title="Property Type"
              />
              <Card.Body>
                <PropertyTypePicker
                  defaultPropertySubtypes={template.parameters.propertySubtypeIds}
                  defaultPropertyTypes={template.parameters.propertyTypeIds}
                  selectAllPropertyTypes={formData.selectAllPropertyTypes}
                  selectedPropertySubtypes={formData.propertySubtypes}
                  selectedPropertyTypes={formData.propertyTypes}
                  onUpdatePropertyType={handleUpdatePropertyType}
                />
              </Card.Body>
            </Card>
            <Card className="mb-7">
              <PanelHeader
                icon={faUsers}
                subtitle="Choose which users are able to create this document"
                title="Users"
              />
              <Card.Body>
                <UserPicker
                  companyId={companyId}
                  defaultUserIds={template.parameters.userIds}
                  selectAllUserIds={formData.selectAllUserIds}
                  selectedUserIds={formData.userIds}
                  onChange={handleUserChange}
                />
              </Card.Body>
            </Card>
            <div>
              {options.length > 0 && (
                <Card className="mb-7" id="default-doc-options">
                  <PanelHeader
                    icon={faWrench}
                    subtitle="Choose which document options are set by default when this document is created"
                    title="Document Options"
                  />
                  <Card.Body>
                    <DefaultOptionMessage/>
                    {options}
                  </Card.Body>
                </Card>
              )}
            </div>
            <Card className="mb-7">
              <PanelHeader
                icon={faFile}
                subtitle="Choose which pages populate by default in a new version of this document"
                title="Pages"
              />
              <Card.Body>
                <PagePicker
                  basePath={basePath}
                  defaultPages={formData.defaultPages?.map(page => ({ ...page, key: page.template_type }))}
                  templateDefaultPageLibrary={template.defaultPageLibrary}
                  templateDefaultPages={template.defaultPages}
                  userCreated={template.userCreated}
                />
              </Card.Body>
            </Card>
            <SaveDocumentTemplateModal
              saving={saving}
              show={showSaveModal}
              templateName={formData.docName || template.name}
              onClose={() => setShowSaveModal(false)}
              onSave={save}
            />
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

function extractPageSettings(formData : DocumentDefaultsType) : PageSettingsType {
  return {
    availablePageTemplateTreeIds: normalizePages(formData.availablePageTemplateTreeIds),
    defaultPages: normalizePages(formData.defaultPages)
  };
}

function normalizePages(pages : DocumentDefaultsType['defaultPages']) {
  if (pages == null) return null;

  return pages.map(({ id, template_type }) => ({ id, template_type }));
}

function pageSettingsChanged(previousSettings : PageSettingsType, nextSettings : PageSettingsType) {
  return JSON.stringify(previousSettings) !== JSON.stringify(nextSettings);
}


const mapStateToProps = ({ formData }) => ({ formData });
const mapDispatchToProps = { setFormData };

export default connect(mapStateToProps, mapDispatchToProps)(DocumentTemplateForm);
